import { KlusModel } from "@personeel/models/KlusModel.ts";
import { useKlusSelectedKlusStore } from "@personeel/stores/SelectedKlusStore.ts";
import { DateTime } from "luxon";
import { RefObject } from "react";
import { bepaalDragMode, DragMode } from "./dragMode";
import { useKlusDragTijd } from "./useKlusDragTijd";
import { useKlusDragTussenDiensten } from "./useKlusDragTussenDiensten";

export interface UseKlusDragOptions {
  klus: KlusModel;
  timeLinePeriode: Periode;
  scale: number;
  /**
   * Optionele callback die bij elke positiewijziging tijdens drag wordt aangeroepen met de nieuwe offsetPx.
   * Gebruik dit om de DOM direct te updaten zonder React re-render per frame.
   */
  onDragOffsetChange?: (offsetPx: number) => void;
  /**
   * Optionele callback die bij elke positiewijziging tijdens drag wordt aangeroepen met de afgeronde minuten.
   * Gebruik dit om de tijdweergave direct via DOM te updaten zonder React re-render per frame.
   */
  onDragMinutesChange?: (minutes: number) => void;
  /**
   * Optionele callback die na afronding van de drag wordt aangeroepen met de definitieve offsetPx.
   * Gebruik dit om de DOM direct te updaten zonder React useEffect.
   * o.a. om bij korte sleepacties van klus het even verspringen van de klus tegen te gaan
   */
  onFinalOffsetSet?: (offsetPx: number) => void;
}

export interface UseKlusDragResult {
  /** Visuele pixel-verschuiving t.o.v. de basispositie tijdens horizontaal slepen */
  dragOffsetPx: number;
  /** True terwijl er gesleept wordt (horizontaal of verticaal) */
  isDragging: boolean;
  /** Synchrone ref: true tijdens actieve drag — gebruik in useEffect guards */
  isDraggingRef: RefObject<boolean>;
  /** onPointerDown handler om op het klus-element te zetten */
  onPointerDown: (e: React.PointerEvent) => void;
}

export type Periode = {
  begin: DateTime;
  eind: DateTime;
};

export type DienstVoorNatijdPeriode = {
  voortijd: number;
  natijd: number;
};

/**
 * Orkestrator-hook voor het verschuiven van een klus.
 * Detecteert na minimaal 5px beweging de dragmode via bepaalDragMode() en
 * delegeert vervolgens naar de juiste drag-hook:
 *   - "tijd"           → useKlusDragTijd    (horizontaal verschuiven in de tijdlijn)
 *   - "tussenDiensten" → useKlusDragTussenDiensten (vertikaal wisselen van dienst)
 */
export function useKlusDrag({
  klus,
  timeLinePeriode,
  scale,
  onDragOffsetChange,
  onDragMinutesChange,
  onFinalOffsetSet,
}: UseKlusDragOptions): UseKlusDragResult {
  const { setSelectedKlus } = useKlusSelectedKlusStore();

  // Horizontale drag-hook (in tijd)
  const tijdDrag = useKlusDragTijd({
    klus,
    timeLinePeriode,
    scale,
    onDragOffsetChange,
    onDragMinutesChange,
    onFinalOffsetSet,
  });
  // Verticale drag-hook (tussen diensten)
  const tussenDienstenDrag = useKlusDragTussenDiensten({ klus });

  const isDragging = tijdDrag.isDragging || tussenDienstenDrag.isDragging;

  function onPointerDown(e: React.PointerEvent) {
    if (e.button !== 0) return; // alleen linkermuisknop

    let dragMode: DragMode | null = null;
    const mouseDownPos = { x: e.clientX, y: e.clientY };

    const target = e.currentTarget as HTMLButtonElement;

    // setPointerCapture zorgt dat alle pointermove/pointerup events naar dit element
    // blijven komen, ook als de pointer het venster verlaat tijdens het slepen
    target.setPointerCapture(e.pointerId);

    function onPointerMove(ev: PointerEvent) {
      const dx = ev.clientX - mouseDownPos.x;
      const dy = ev.clientY - mouseDownPos.y;

      if (dragMode === null) {
        const bepaaldeDragMode = bepaalDragMode(dx, dy, klus);
        if (bepaaldeDragMode !== null) {
          setSelectedKlus(null);
          dragMode = bepaaldeDragMode;
          if (bepaaldeDragMode === "tijd") {
            tijdDrag.startDrag(
              mouseDownPos.x,
              document.getElementById("right-div")?.scrollLeft ?? 0,
            );
          } else {
            tussenDienstenDrag.startDrag(mouseDownPos.x, mouseDownPos.y);
          }
        }
      }

      if (dragMode === "tijd") tijdDrag.onDragMove(ev.clientX);
      else if (dragMode === "tussenDiensten") tussenDienstenDrag.onDragMove(ev.clientX, ev.clientY);
    }

    function onPointerUp(ev: PointerEvent) {
      target.removeEventListener("pointermove", onPointerMove);
      target.removeEventListener("pointerup", onPointerUp);
      target.releasePointerCapture(ev.pointerId);

      if (dragMode === "tijd") {
        tijdDrag.onDragEnd();
      } else if (dragMode === "tussenDiensten") {
        tussenDienstenDrag.onDragEnd(ev.clientY);
      } else {
        // Geen drag maar klik: klus selecteren voor sidePanel
        tijdDrag.cancelDrag();
        tussenDienstenDrag.cancelDrag();
        setSelectedKlus(klus);
      }
    }

    target.addEventListener("pointermove", onPointerMove);
    target.addEventListener("pointerup", onPointerUp);
  }

  return {
    dragOffsetPx: tijdDrag.dragOffsetPx,
    isDragging,
    isDraggingRef: tijdDrag.isDraggingRef,
    onPointerDown,
  };
}
